import { useEffect, useRef, useState } from 'react';

import {
    NO_HIGHLIGHTED_OPTION,
    getInitialHighlightedOptionIndex,
} from '@/caregiverSpace/shared/lib';
import { DropDownOption } from '@/caregiverSpace/shared/model';

type UseHighlightedOptionProps<TOption> = {
    isFocused: boolean;
    filteredOptions: DropDownOption<TOption>[];
    selectedOption?: DropDownOption<TOption> | null;
    scrollRoot: Element | null;
};

export const useHighlightedOption = <TOption>({
    isFocused,
    filteredOptions,
    selectedOption,
    scrollRoot,
}: UseHighlightedOptionProps<TOption>) => {
    const [highlightedOptionIndex, setHighlightedOptionIndex] = useState(
        NO_HIGHLIGHTED_OPTION,
    );
    const optionRefs = useRef<Array<HTMLDivElement | null>>([]);

    useEffect(() => {
        if (!isFocused) {
            setHighlightedOptionIndex(NO_HIGHLIGHTED_OPTION);
        }
    }, [isFocused]);

    useEffect(() => {
        if (!isFocused) {
            return;
        }

        setHighlightedOptionIndex((current) => {
            if (current !== NO_HIGHLIGHTED_OPTION) {
                return current;
            }

            return getInitialHighlightedOptionIndex(
                filteredOptions,
                selectedOption,
            );
        });
    }, [filteredOptions, isFocused, selectedOption]);

    useEffect(() => {
        const highlightedOptionRef = optionRefs.current[highlightedOptionIndex];

        if (!highlightedOptionRef || !scrollRoot) {
            return;
        }

        const rect = highlightedOptionRef.getBoundingClientRect();
        const scrollRect = scrollRoot.getBoundingClientRect();

        const isVisible =
            rect.top >= scrollRect.top && rect.bottom <= scrollRect.bottom;

        if (!isVisible) {
            highlightedOptionRef.scrollIntoView({
                block: 'nearest',
            });
        }
    }, [highlightedOptionIndex, scrollRoot]);

    return {
        highlightedOptionIndex,
        setHighlightedOptionIndex,
        optionRefs,
    };
};
